// The account key's recovery file (D41): the PKCS#8 private key encrypted under a key
// derived from a passphrase the user chooses, offered for download once, when the key is
// generated. It is the only copy outside the browser; whoever has the file and the
// passphrase holds the account key.
//
// PBKDF2-SHA-256 (≥ 600,000 iterations, 16-byte salt) derives an AES-256-GCM key; the
// ciphertext carries GCM's tag. The additional data binds the format name and the public
// key, so a file whose public key was swapped does not decrypt.
import { messages } from "../i18n";
import { base64ToBytes, bytesEqual, bytesToBase64, concat, owned, randomBytes, utf8 } from "../proto/bytes";
import { ACCOUNT_ALG, type AccountKey, importAccountKey, publicKeyOfPkcs8 } from "./key";

export const RECOVERY_FORMAT = "ocs-account-key-recovery";
export const MIN_PASSPHRASE_LENGTH = 12;
export const PBKDF2_ITERATIONS = 600_000;

export interface RecoveryFile {
  format: typeof RECOVERY_FORMAT;
  version: 1;
  alg: typeof ACCOUNT_ALG;
  /** Uncompressed SEC1 point, base64. */
  public_key: string;
  /** Lowercase hex; what `ocs account pin` shows. */
  fingerprint: string;
  kdf: { name: "PBKDF2-SHA-256"; iterations: number; salt: string };
  cipher: { name: "AES-256-GCM"; iv: string; ciphertext: string };
  created_at: string;
}

export class RecoveryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RecoveryError";
  }
}

/** Why a passphrase is unacceptable, or undefined when it is fine. */
export function passphraseProblem(passphrase: string, confirmation: string): string | undefined {
  const e = messages().account.errors;
  if (passphrase.length < MIN_PASSPHRASE_LENGTH) return e.passphraseTooShort(MIN_PASSPHRASE_LENGTH);
  if (passphrase !== confirmation) return e.passphrasesDiffer;
  return undefined;
}

async function wrappingKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", owned(utf8.encode(passphrase)), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: owned(salt), iterations },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function additionalData(publicKey: Uint8Array): Uint8Array<ArrayBuffer> {
  return owned(concat(utf8.encode(`${RECOVERY_FORMAT}/1`), new Uint8Array([0]), publicKey));
}

export async function createRecoveryFile(
  pkcs8: Uint8Array,
  publicKey: Uint8Array,
  fingerprint: string,
  passphrase: string,
  now = new Date(),
): Promise<RecoveryFile> {
  if (passphrase.length < MIN_PASSPHRASE_LENGTH) throw new RecoveryError(messages().account.errors.passphraseTooShort(MIN_PASSPHRASE_LENGTH));
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await wrappingKey(passphrase, salt, PBKDF2_ITERATIONS);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: owned(iv), additionalData: additionalData(publicKey) },
      key,
      owned(pkcs8),
    ),
  );
  return {
    format: RECOVERY_FORMAT,
    version: 1,
    alg: ACCOUNT_ALG,
    public_key: bytesToBase64(publicKey),
    fingerprint,
    kdf: { name: "PBKDF2-SHA-256", iterations: PBKDF2_ITERATIONS, salt: bytesToBase64(salt) },
    cipher: { name: "AES-256-GCM", iv: bytesToBase64(iv), ciphertext: bytesToBase64(ciphertext) },
    created_at: now.toISOString(),
  };
}

/** Parse the text of a recovery file, checking its shape and parameters. */
export function parseRecoveryFile(text: string): RecoveryFile {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    throw new RecoveryError(messages().account.errors.notJson);
  }
  const e = messages().account.errors;
  const f = v as Partial<RecoveryFile> | null;
  if (!f || f.format !== RECOVERY_FORMAT || f.version !== 1 || f.alg !== ACCOUNT_ALG) {
    throw new RecoveryError(e.notRecoveryFile);
  }
  if (f.kdf?.name !== "PBKDF2-SHA-256" || !(f.kdf.iterations >= PBKDF2_ITERATIONS) || typeof f.kdf.salt !== "string") {
    throw new RecoveryError(e.unsupportedKdf);
  }
  if (f.cipher?.name !== "AES-256-GCM" || typeof f.cipher.iv !== "string" || typeof f.cipher.ciphertext !== "string") {
    throw new RecoveryError(e.unsupportedCipher);
  }
  if (typeof f.public_key !== "string" || typeof f.fingerprint !== "string") {
    throw new RecoveryError(e.noPublicKey);
  }
  return f as RecoveryFile;
}

/**
 * Decrypt a recovery file and import its key non-extractable. Throws RecoveryError on a
 * wrong passphrase, a tampered file, or a private key that does not match the public key.
 */
export async function openRecoveryFile(file: RecoveryFile, passphrase: string): Promise<AccountKey> {
  const publicKey = base64ToBytes(file.public_key);
  const key = await wrappingKey(passphrase, base64ToBytes(file.kdf.salt), file.kdf.iterations);
  let pkcs8: Uint8Array;
  try {
    pkcs8 = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: owned(base64ToBytes(file.cipher.iv)), additionalData: additionalData(publicKey) },
        key,
        owned(base64ToBytes(file.cipher.ciphertext)),
      ),
    );
  } catch {
    throw new RecoveryError(messages().account.errors.wrongPassphrase);
  }
  if (!bytesEqual(await publicKeyOfPkcs8(pkcs8), publicKey)) {
    throw new RecoveryError(messages().account.errors.keyMismatch);
  }
  return importAccountKey(pkcs8, publicKey);
}
